import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useNavigate } from '@tanstack/react-router'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { PresentationChartLineIcon } from '@gto/icons-react/outline/presentation-chart-line'
import { ShoppingCartIcon } from '@gto/icons-react/outline/shopping-cart'
import { TrophyIcon } from '@gto/icons-react/outline/trophy'
import { UsersIcon } from '@gto/icons-react/outline/users'
import { ToggleButton, ToggleButtonGroup } from '@gto/ui/toggle-button'

import type { AffiliatePartnerLeaderboardRowSchema } from '@/api/gen'
import {
  useGetNextBatchView,
  useGetProgramAnalyticsView,
  useGetProgramLeaderboardView,
  useGetProgramStatsView,
} from '@/api/gen'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { DataTable } from '@/components/data-table'
import { InlineQueryError } from '@/components/inline-query-error'
import { useLeaderboardColumns } from '@/components/leaderboard-table'
import { NextBatchCard } from '@/components/next-batch-card'
import { Page } from '@/components/page'
import { PerformanceChart } from '@/components/performance-chart'
import { StatsCard } from '@/components/stats-card'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { isGranularity, useGranularitySnapshot } from '@/utils/granularity'
import { toAmount } from '@/utils/to-amount'
import { toNullablePct } from '@/utils/to-nullable-pct'

const getLeaderboardRowId = (row: AffiliatePartnerLeaderboardRowSchema) => row.partner_id

export const AdminDashboardPage = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  // Snapshot the date window so `getDateRange()`'s `new Date()` runs on toggle,
  // not every render — keeps the TanStack Query key identity stable across
  // unrelated rerenders.
  const { granularity, dateRange, setGranularity } = useGranularitySnapshot('monthly')

  const statsQuery = useGetProgramStatsView(dateRange)
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = statsQuery
  const analyticsQuery = useGetProgramAnalyticsView({ ...dateRange, granularity })
  const { data: analytics, isError: analyticsError, refetch: refetchAnalytics } = analyticsQuery
  const leaderboardQuery = useGetProgramLeaderboardView({
    ...dateRange,
    sort_by: 'revenue',
    limit: 10,
  })
  const {
    data: leaderboard,
    isLoading: leaderboardLoading,
    isError: leaderboardError,
    refetch: refetchLeaderboard,
  } = leaderboardQuery
  const nextBatchQuery = useGetNextBatchView()
  const {
    data: nextBatch,
    isLoading: nextBatchLoading,
    isError: nextBatchError,
    refetch: refetchNextBatch,
  } = nextBatchQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports only on TERMINAL failure so TanStack retries don't multiply events.
  useReportQueryError(statsQuery, 'getProgramStatsView failed: ')
  useReportQueryError(analyticsQuery, 'getProgramAnalyticsView failed: ')
  useReportQueryError(leaderboardQuery, 'getProgramLeaderboardView failed: ')
  useReportQueryError(nextBatchQuery, 'getNextBatchView failed: ')

  const metricsError = statsError || analyticsError

  // Analytics API returns rows newest-first; charts and sparklines read
  // chronologically (oldest on the left), so reverse a copy here.
  const analyticsRows = [...(analytics?.rows ?? [])].reverse()
  const ordersSeries = analyticsRows.map((row) => row.orders)
  const revenueSeries = analyticsRows.map((row) =>
    toAmount(row.revenue_usd, 'dashboard analytics revenue_usd:'),
  )
  const commissionsSeries = analyticsRows.map((row) =>
    toAmount(row.commission_usd, 'dashboard analytics commission_usd:'),
  )

  const totalRevenue = toAmount(stats?.total_revenue_usd, 'dashboard total_revenue_usd:')
  const totalCommissions = toAmount(
    stats?.total_commissions_usd,
    'dashboard total_commissions_usd:',
  )

  const chartData = analyticsRows.map((row) => ({
    label: row.period_label,
    revenue: toAmount(row.revenue_usd, 'dashboard analytics revenue_usd:'),
    commissions: toAmount(row.commission_usd, 'dashboard analytics commission_usd:'),
  }))

  const leaderboardRows = leaderboard?.rows ?? []
  const columns = useLeaderboardColumns()

  return (
    <Page>
      <Page.Header title={<FormattedMessage id="admin.dashboard.title" />} />

      <Page.Content>
        {metricsError ? (
          <InlineQueryError
            data-tst="dashboard-metrics-error"
            messageId="admin.dashboard.error"
            onRetry={() => {
              if (statsError) {
                void refetchStats()
              }
              if (analyticsError) {
                void refetchAnalytics()
              }
            }}
          />
        ) : (
          <>
            <div
              className="flex flex-row flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<UsersIcon />}
                label={<FormattedMessage id="admin.dashboard.activePartners" />}
                loading={statsLoading}
                tooltip={formatMessage({ id: 'tooltip.activePartners' })}
                value={<FormattedNumber value={stats?.active_partners ?? 0} />}
                deltaPct={toNullablePct(
                  stats?.active_partners_delta_pct,
                  'admin-dashboard toNullablePct active_partners_delta_pct:',
                )}
              />
              <DashboardKpiTile
                icon={<ShoppingCartIcon />}
                label={<FormattedMessage id="admin.dashboard.totalOrders" />}
                loading={statsLoading}
                sparklineData={ordersSeries}
                tooltip={formatMessage({ id: 'tooltip.totalOrders' })}
                value={<FormattedNumber value={stats?.total_orders ?? 0} />}
                deltaPct={toNullablePct(
                  stats?.orders_delta_pct,
                  'admin-dashboard toNullablePct orders_delta_pct:',
                )}
                onClick={() => {
                  // Drill into the orders behind the number — same payment-date
                  // window the tile's stats query uses.
                  void navigate({
                    to: '/admin/orders',
                    search: { from: dateRange.date_from, to: dateRange.date_to },
                  })
                }}
              />
              <DashboardKpiTile
                icon={<CurrencyDollarIcon />}
                label={<FormattedMessage id="admin.dashboard.totalRevenue" />}
                loading={statsLoading}
                sparklineData={revenueSeries}
                tooltip={formatMessage({ id: 'tooltip.totalRevenue' })}
                deltaPct={toNullablePct(
                  stats?.revenue_delta_pct,
                  'admin-dashboard toNullablePct revenue_delta_pct:',
                )}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalRevenue}
                  />
                }
              />
              <DashboardKpiTile
                icon={<BanknotesIcon />}
                label={<FormattedMessage id="admin.dashboard.totalCommissions" />}
                loading={statsLoading}
                sparklineData={commissionsSeries}
                tooltip={formatMessage({ id: 'tooltip.totalCommissions' })}
                deltaPct={toNullablePct(
                  stats?.commissions_delta_pct,
                  'admin-dashboard toNullablePct commissions_delta_pct:',
                )}
                value={
                  <FormattedNumber
                    currency="USD"
                    maximumFractionDigits={0}
                    style="currency"
                    value={totalCommissions}
                  />
                }
              />
            </div>

            <PerformanceChart
              data={chartData}
              icon={<PresentationChartLineIcon />}
              title={<FormattedMessage id="dashboard.performance" />}
              controls={
                <ToggleButtonGroup
                  size="small"
                  value={granularity}
                  onValueChange={(v) => {
                    if (isGranularity(v)) {
                      setGranularity(v)
                    }
                  }}
                >
                  <ToggleButton value="daily">
                    {formatMessage({ id: 'dashboard.granularity.daily' })}
                  </ToggleButton>
                  <ToggleButton value="weekly">
                    {formatMessage({ id: 'dashboard.granularity.weekly' })}
                  </ToggleButton>
                  <ToggleButton value="monthly">
                    {formatMessage({ id: 'dashboard.granularity.monthly' })}
                  </ToggleButton>
                </ToggleButtonGroup>
              }
              totals={[
                {
                  id: 'revenue',
                  label: <FormattedMessage id="admin.dashboard.revenueThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalRevenue}
                    />
                  ),
                },
                {
                  id: 'commissions',
                  label: <FormattedMessage id="admin.dashboard.commissionsThisPeriod" />,
                  value: (
                    <FormattedNumber
                      currency="USD"
                      maximumFractionDigits={0}
                      style="currency"
                      value={totalCommissions}
                    />
                  ),
                },
              ]}
            />
          </>
        )}

        {nextBatchError ? (
          <InlineQueryError
            data-tst="dashboard-next-batch-error"
            messageId="admin.dashboard.error"
            onRetry={() => void refetchNextBatch()}
          />
        ) : (
          <NextBatchCard
            data={nextBatch}
            loading={nextBatchLoading}
          />
        )}

        <StatsCard
          icon={<TrophyIcon />}
          title={<FormattedMessage id="admin.dashboard.topPartners" />}
        >
          {leaderboardError ? (
            <InlineQueryError
              data-tst="dashboard-leaderboard-error"
              messageId="admin.dashboard.error"
              onRetry={() => void refetchLeaderboard()}
            />
          ) : (
            <DataTable<AffiliatePartnerLeaderboardRowSchema>
              columns={columns}
              data={leaderboardRows}
              getRowId={getLeaderboardRowId}
              loading={leaderboardLoading}
              onRowClick={(row) => {
                void navigate({
                  to: '/admin/partners/$partnerId',
                  params: { partnerId: getLeaderboardRowId(row) },
                })
              }}
            />
          )}
        </StatsCard>
      </Page.Content>
    </Page>
  )
}
